import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePersona } from '@/auth/usePersona';
import { useGetRegionConfigQuery } from '@store/api';
import { footerConfig, REGION_HELP_LINK_KEYS } from '@config/footerConfig';
import type { ContactLink } from '@config/footerConfig';
import type { QuickLink } from '@store/models';

const REGION_NAME = 'NORAM';

/** Route prefixes that make up the Planner module (vs. the Employee module). */
const PLANNER_MODULE_PREFIXES = ['/planner', '/reports', '/admin'];

function isSafeTarget(value: string, type: string): boolean {
  if (type === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function mapRegionHelpLink(link: QuickLink): ContactLink | null {
  const keys = REGION_HELP_LINK_KEYS[link.name];
  if (!keys) return null;
  if (!isSafeTarget(link.value, link.type)) return null;
  return {
    ...keys,
    target: link.value,
    type: link.type === 'email' ? 'email' : 'url',
  };
}

/**
 * Spec 097-footer-help-revamp: merges the static footer help-link config with
 * region-API help links into a single list of Help-panel Contacts.
 *
 * Deduplicates by (descriptionKey, linkLabelKey) so a region that re-supplies
 * the static NORAM contact does not produce a duplicate row.
 */
export function useContactLinks(): ContactLink[] {
  const { isPlanner, isAdmin } = usePersona();
  const { pathname } = useLocation();
  const { data: regionData } = useGetRegionConfigQuery(REGION_NAME);

  return useMemo(() => {
    const regionHelpLinks =
      isPlanner || isAdmin
        ? regionData?.data?.plannerHelpLinks
        : regionData?.data?.employeeHelpLinks;

    const mappedLinks: ContactLink[] = (regionHelpLinks ?? [])
      .map(mapRegionHelpLink)
      .filter((link): link is ContactLink => link !== null);

    const apiTargets = new Set(mappedLinks.map((l) => l.target.toLowerCase()));
    const fallbackLinks = footerConfig.helpLinks.filter(
      (cfgLink) => !apiTargets.has(cfgLink.target.toLowerCase())
    );

    const combined = [...mappedLinks, ...fallbackLinks].filter((l) => l.target.length > 0);

    // The NORAM planner support DL belongs to the Planner module only; the
    // Employee module Help section must not show it, regardless of persona.
    const isPlannerModule = PLANNER_MODULE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );

    const seen = new Set<string>();
    const deduped: ContactLink[] = [];
    for (const link of combined) {
      if (!isPlannerModule && link.descriptionKey === 'helpPanel.contacts.noramSupportDescription') {
        continue;
      }
      const key = `${link.descriptionKey}::${link.linkLabelKey}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(link);
    }
    return deduped;
  }, [isPlanner, isAdmin, regionData, pathname]);
}
